import { constants } from '@config';
import * as TextStylingService from 'src/services/text-styling';

import AbstractElement from './abstractElement';
import { TextOverflowChainHandler } from '../factory/text-overflow/chainHandler';
import { logError } from 'src/utils/logger';
import { AdvancedOrderedListChainHandler } from 'src/factories/advanced-ordered-list/chain-handler';

export default class TextRenderer extends AbstractElement {
  constructor (text, canvas, parent) {
    super();

    this.text = text;
    this.parent = parent;
    this.canvas = canvas;

    this.bbox = {};
    this.paragraphParams = {};
    this.orderTracking = {};

    this.isTableText = false;
    this.isShortText = false;

    this.newX = 0;
    this.newY = 0;
    this.oldX = 0;
    this.oldY = 0;

    this.lineIndex = -1;

    this.shouldAbortRendering = false;
    this.blankArray = [];
    this.textOverflowChainHandler = new TextOverflowChainHandler();
    this.advancedOrderedListChainHandler = new AdvancedOrderedListChainHandler();

    this.autoAdjustableFlag = false;
  }

  initialize () {
    this.isTableText =
      this.parent.dom.parentElement &&
      this.parent.dom.parentElement.classList.contains('table-cell');

    this.isShortText = this.text.textContent.length <= 100;

    this.bbox = this.parent.getBBoxAligned();
    this.oldY = this.bbox.y;

    this.autoAdjustableFlag = this.text.isTextSizeAutoAdjustable(this.parent);

    return this;
  }

  #checkTextIsContinuing () {
    if (!this.parent.parent_text) return;

    const parentLinkedText = this.textOverflowChainHandler.getTextDomByLinkId(this.parent.parent_text);

    if (!parentLinkedText) {
      logError('checkTextIsContinuing')(new Error('Text is rendered before parent text is fetched'));
      return;
    }

    this.text.isContinuing = !parentLinkedText.querySelector('text:last-child')?.classList?.contains('eol');

    const orderedData = this.textOverflowChainHandler.getOrderedDataFromPreviousPages(this.parent.parent_text);
    this.orderTracking = TextStylingService.calculateOrderTracking(orderedData.level, orderedData.startFrom, orderedData.style);
  }

  #calculateAdvancedOrderedListTracking () {
    const prevOrderedData = INKIVE.AdvancedOrderedListFactory.orderedData;
    const currentPageOrderedData = this.advancedOrderedListChainHandler.getElementOrderedDataOnCurrentPage(this.parent.id);

    const orderedData = {
      style: prevOrderedData.style.concat(currentPageOrderedData.style),
      startFrom: prevOrderedData.startFrom.concat(currentPageOrderedData.startFrom),
      level: prevOrderedData.level.concat(currentPageOrderedData.level)
    };

    const advancedOrderedListTracking = TextStylingService.calculateOrderTracking(orderedData.level, orderedData.startFrom, orderedData.style);

    for (const styleId in advancedOrderedListTracking) {
      if (this.orderTracking?.[styleId]) delete this.orderTracking?.[styleId];

      this.orderTracking[styleId] = advancedOrderedListTracking[styleId];
    }
  }

  #calculateTextAttributeArray () {
    const text = this.text;
    if (!text.shouldRecalculateStyle) return;

    text.alignmentArray = [];
    text.triggeredArray = [];
    text.bulletArray = [];
    text.bulletStyleArray = [];
    text.orderedArray = [];
    text.orderedStyleArray = [];
    text.orderedNumberingStyleArray = [];
    text.orderedRegexArray = [];
    text.orderedStartArray = [];
    text.headingArray = [];

    for (let i = 0; i < text.strings.length; i++) {
      const string = text.strings[i];
      if (string.isEol || i === text.strings.length - 1) {
        text.alignmentArray.push(string.textAnchor);
        text.triggeredArray.push(string.isButtonTriggered);
        text.bulletArray.push(string.bulletLevel);
        text.bulletStyleArray.push(string.bulletStyle);
        text.orderedArray.push(string.orderedLevel);
        text.orderedStyleArray.push(string.orderedStyle);
        text.orderedStartArray.push(string.orderedStart);
        text.headingArray.push(string.heading);
      }

      if (i === 0 || (i > 0 && text.strings[i - 1].isEol)) {
        text.customBulletSymbolArray.push(string.customBulletSymbol);
        text.bulletFillArray.push(string.bulletFill);
        text.bulletFontSizeArray.push(string.bulletFontSize);
        text.bulletFontStyleArray.push(string.bulletFontStyle);
        text.bulletHighlightArray.push(string.bulletHighlight);
        text.bulletWidthArray.push(string.bulletWidth);
        text.bulletFontFamilyArray.push(string.bulletFontFamily);
        text.bulletCustomFontIdArray.push(string.bulletCustomFontId);

        text.orderedFillArray.push(string.orderedFill);
        text.orderedFontSizeArray.push(string.orderedFontSize);
        text.orderedWidthArray.push(string.orderedWidth);
        text.orderedFontFamilyArray.push(string.orderedFontFamily);
        text.orderedFontWeightArray.push(string.orderedFontWeight);
        text.orderedFontStyleArray.push(string.orderedFontStyle);
        text.orderedHighlightArray.push(string.orderedHighlight);
        text.orderedCustomFontIdArray.push(string.orderedCustomFontId);
        text.orderedNumberingStyleArray.push(string.orderedNumberingStyle);
        text.orderedRegexArray.push(string.orderedRegex);
      }
    }
  }

  #emptyStringDOM () {
    this.text.dom.innerHTML = '';
    this.text.strings = [];
  }

  #handleTransform () {
    const text = this.text;
    const transformList = this.canvas.getTransformList(
      text.dom.parentElement
    );

    const isTransformed =
      transformList.numberOfItems === 1 &&
      transformList.getItem(0).type === 4;

    if (!isTransformed) return;

    const xForm = transformList.getItem(0);
    transformList.removeItem(0);
    transformList.appendItem(xForm);
  }

  #setBBox () {
    this.text.rectBBox = this.parent.getBBox();
  }

  #sanitizeSpace () {
    // Replace all spaces with non-breaking spaces.
    this.text.textContent = this.text.textContent.replace(/ /g, constants.text.noBreakSpace);
  }

  #fakeCharIndexToReal (index) {
    const { fakeCharsIndices } = this.text;
    if (!fakeCharsIndices || !fakeCharsIndices.length) return index;

    let realIndex = index;
    for (let i = 0; i < fakeCharsIndices.length; i++) {
      if (fakeCharsIndices[i] >= index) break;
      realIndex--;
    }

    return realIndex;
  }

  #realCharIndexToFake (index) {
    const fakeCharsIndices = this.text.fakeCharsIndices;
    if (!fakeCharsIndices || !fakeCharsIndices.length) return index;

    let fakeIndex = index;
    for (let i = 0; i < fakeCharsIndices.length; i++) {
      if (fakeCharsIndices[i] < fakeIndex) fakeIndex++;
      else break;
    }

    return fakeIndex;
  }

  #shiftLetterSpacingWhenEdit () {
    const text = this.text;
    text.letter_spacing = TextStylingService.shiftLetterSpacing(text.oldContent, text.textContent, text.cursorPosition, text.newLetters, text.letter_spacing);
  }

  #shiftLetterSpacingWhenTypewriter (difference) {
    const text = this.text;
    const addedLetterSpacing = Array.from({ length: difference.added }).fill(0);
    const startIndex = this.#fakeCharIndexToReal(difference.position);
    const endIndex = this.#fakeCharIndexToReal(difference.removed);
    text.letter_spacing.splice(startIndex, endIndex, ...addedLetterSpacing);
  }

  #handleLetterSpacing (difference) {
    const text = this.text;

    // TODO: Split this function
    const shouldShiftLetterSpacing = text.newLetters && Array.isArray(text.letter_spacing);
    if (!shouldShiftLetterSpacing) return;

    if (!Number.isNaN(text.cursorPosition)) {
      this.#shiftLetterSpacingWhenEdit();
    } else if (!text.cursorPosition) {
      this.#shiftLetterSpacingWhenTypewriter(difference);
    }
  }

  #addAttribute (text, attribute, changes, defaultValue, blankArray) {
    const previousLine = changes.lines[0] - 1;
    const newValue = attribute === 'orderedStartArray'
      ? defaultValue
      : text[attribute][previousLine] || defaultValue;

    for (let i = changes.lines.length - 1; i >= 0; i--) {
      text[attribute].splice(changes.lines[i], 0, newValue);
      if (attribute === 'paragraph_spacing' && previousLine >= 0) {
        text[attribute][previousLine] = Math.max(text[attribute][previousLine], 0);
      }
    }
  }

  #removeAttribute (text, attribute, changes) {
    const indexToDelete = changes.lines[0] - 1 >= 0 && attribute === 'paragraph_spacing'
      ? changes.lines[0] - 1
      : changes.lines[0];
    text[attribute].splice(indexToDelete, changes.lines.length);
  }

  #handleCommonTextSpacing (difference) {
    const text = this.text;
    const attributeMap = constants.text.paragraphAttributesMap;

    const actionMap = {
      added: this.#addAttribute,
      removed: this.#removeAttribute
    };

    const cursorPosition = typeof text.cursorPosition === 'number' ? text.cursorPosition : difference.position + text.newLetters;
    const changes = INKIVE.Text.Helpers.getChangedLines(text.oldContent, text.textContent, cursorPosition, text.newLetters);

    for (const attribute in attributeMap) {
      if (!Array.isArray(text[attribute])) continue;

      actionMap[changes.mode]?.(text, attribute, changes, attributeMap[attribute], this.blankArray);
    }
  }

  #handleShiftingAttributes (paragraphs) {
    this.blankArray = paragraphs.map(paragraph => paragraph === '' || paragraph === constants.text.noBreakSpace);
    if (!this.text.shouldRecalculateStyle) return;

    const difference = INKIVE.Tool.calculateStringContentDifference(this.text.oldContent, this.text.textContent);
    this.#handleLetterSpacing(difference);
    this.#handleCommonTextSpacing(difference);
  }

  #setAttributeFromArray (attributeName, spacingArray) {
    if (!Array.isArray(spacingArray)) return;
    this.text.dom.setAttribute(attributeName, spacingArray.join(' '));
  }

  #setDataAttributeToDOM () {
    this.#setAttributeFromArray('data-dy', this.text.line_spacing);
    this.#setAttributeFromArray('data-margin', this.text.line_margin);
    this.#setAttributeFromArray('data-paragraph-dy', this.text.paragraph_spacing);
    this.#setAttributeFromArray('data-first-line-margin', this.text.first_line_margin);
    this.#setAttributeFromArray('data-right-margin', this.text.right_margin);

    this.#setOrderedStartToDOM();
  }

  #setOrderedStartToDOM () {
    const hasOrder = this.text.orderedArray.some(Number);

    if (hasOrder && this.text.orderedStart) {
      this.text.dom.dataset.orderedStart = this.text.orderedStart;
    }
  }

  #calculateLineMargin (paragraphIndex, bulletWidth, orderedWidth) {
    const text = this.text;

    if (!text.line_margin) return 0;

    const lineMargin = Array.isArray(text.line_margin)
      ? text.line_margin[paragraphIndex]
      : text.line_margin;

    const maxMargin = text.rectBBox.width - text.font_size * constants.text.relativeFontWidth - bulletWidth - orderedWidth;

    return lineMargin > maxMargin
      ? maxMargin
      : lineMargin || 0;
  }

  #calculateFirstLineMargin (paragraphIndex, bulletWidth, orderedWidth) {
    const text = this.text;

    if (!text.first_line_margin) return 0;

    const firstLineMargin = Array.isArray(text.first_line_margin)
      ? text.first_line_margin[paragraphIndex]
      : text.first_line_margin;

    const maxMargin = text.rectBBox.width - text.font_size * constants.text.relativeFontWidth - bulletWidth - orderedWidth;

    return firstLineMargin > maxMargin
      ? maxMargin
      : firstLineMargin || 0;
  }

  #calculateRightMargin (paragraphIndex, bulletWidth, orderedWidth) {
    const text = this.text;

    if (!text.right_margin) return 0;

    const rightMargin = Array.isArray(text.right_margin)
      ? text.right_margin[paragraphIndex]
      : text.right_margin;

    const maxMargin = text.rectBBox.width - text.font_size * constants.text.relativeFontWidth - bulletWidth - orderedWidth;

    return rightMargin > maxMargin
      ? maxMargin
      : rightMargin || 0;
  }

  #calculateParagraphSpacing (paragraphIndex) {
    const text = this.text;

    if (!text.paragraph_spacing) return 0;
    if (paragraphIndex === 0) return 0;

    return Array.isArray(text.paragraph_spacing)
      ? text.paragraph_spacing.slice(0, paragraphIndex).reduce((accumulator, curr) => accumulator + curr, 0)
      : paragraphIndex * text.paragraph_spacing;
  }

  #calculateLineSpacing (paragraphIndex) {
    const text = this.text;

    if (!text.line_spacing) return 0;

    const lineSpacing = Array.isArray(text.line_spacing)
      ? text.line_spacing[paragraphIndex]
      : text.line_spacing;

    return lineSpacing || 0;
  }

  #calculateLineFactor (mainTextAnchor) {
    const reverseFactor = this.text.direction === 'rtl' ? -1 : 1;
    const alignmentFactor = {
      start: reverseFactor,
      end: -reverseFactor,
      middle: 1,
      justify_all: 1,
      justify_start: 1,
      justify_end: -1,
      justify_middle: 1
    };

    return Number.isNaN(alignmentFactor[mainTextAnchor])
      ? 1
      : alignmentFactor[mainTextAnchor];
  }

  #calculateParagraphParams (paragraphIndex, isLastParagraph) {
    const text = this.text;
    const {
      alignmentArray,
      headingArray,
      bulletArray,
      bulletStyleArray,
      bulletFillArray,
      bulletFontSizeArray,
      bulletHighlightArray,
      bulletFontStyleArray,
      bulletWidthArray,
      bulletFontFamilyArray,
      bulletCustomFontIdArray,
      orderedArray,
      orderedStyleArray,
      orderedNumberingStyleArray,
      orderedRegexArray,
      orderedStartArray,
      orderedFontSizeArray,
      orderedFillArray,
      orderedWidthArray,
      orderedFontFamilyArray,
      orderedFontWeightArray,
      orderedFontStyleArray,
      orderedHighlightArray,
      orderedCustomFontIdArray,
      triggeredArray,
      isLastLineEol,
      text_anchor: textAnchor
    } = this.text;

    const startIndex = 0;
    const lineAnchor = alignmentArray[paragraphIndex] || '';
    const lineBullet = bulletArray[paragraphIndex] || 0;
    const lineOrdered = orderedArray[paragraphIndex] || 0;
    const lineBulletStyle = bulletStyleArray[paragraphIndex] || 0;
    const lineOrderedStyle = orderedStyleArray[paragraphIndex] || 0;
    const lineOrderedNumberingStyle = orderedNumberingStyleArray[paragraphIndex] || 0;
    const lineOrderedRegex = orderedRegexArray[paragraphIndex] || '';
    const isButtonTriggered = triggeredArray[paragraphIndex] || false;
    const heading = headingArray[paragraphIndex] || false;
    const mainTextAnchor = lineAnchor || textAnchor;
    const bulletWidth = lineBullet ? bulletWidthArray[paragraphIndex] : 0;
    const bulletContent = this.bulletContentArray[paragraphIndex] || '•';
    const bulletFill = bulletFillArray[paragraphIndex] || false;
    const bulletFontSize = bulletFontSizeArray[paragraphIndex] || 0;
    const bulletHighlight = bulletHighlightArray[paragraphIndex] || false;
    const bulletFontStyle = bulletFontStyleArray[paragraphIndex] || false;
    const bulletFontFamily = bulletFontFamilyArray[paragraphIndex] || false;
    const bulletCustomFontId = bulletCustomFontIdArray[paragraphIndex] || false;
    const order = this.orderArray[paragraphIndex] || 0;
    const orderedWidth = lineOrdered ? orderedWidthArray[paragraphIndex] : 0;
    const orderedFill = orderedFillArray[paragraphIndex] || false;
    const orderedStart = orderedStartArray[paragraphIndex] || false;
    const orderedFontSize = orderedFontSizeArray[paragraphIndex] || 0;
    const orderedFontFamily = orderedFontFamilyArray[paragraphIndex] || false;
    const orderedFontWeight = orderedFontWeightArray[paragraphIndex] || false;
    const orderedFontStyle = orderedFontStyleArray[paragraphIndex] || false;
    const orderedHighlight = orderedHighlightArray[paragraphIndex] || false;
    const orderedCustomFontId = orderedCustomFontIdArray[paragraphIndex] || false;
    const factor = this.#calculateLineFactor(mainTextAnchor);
    const lineMargin = this.#calculateLineMargin(paragraphIndex, bulletWidth, orderedWidth);
    const firstLineMargin = this.#calculateFirstLineMargin(paragraphIndex, bulletWidth, orderedWidth);
    const rightMargin = this.#calculateRightMargin(paragraphIndex, bulletWidth, orderedWidth);
    const paragraphSpacing = this.#calculateParagraphSpacing(paragraphIndex);
    const lineSpacing = this.#calculateLineSpacing(paragraphIndex);
    const isEol = isLastParagraph ? isLastLineEol : true;
    let maxWidth = text.rectBBox.width;
    if (lineBullet) {
      maxWidth -= (bulletWidth || text.font_size);
      maxWidth = Math.max(maxWidth, text.font_size * constants.text.relativeFontWidth);
    } else if (lineOrdered) {
      maxWidth -= (text.calculateOrderedEntryWidth(order, text.font_size, orderedWidthArray[paragraphIndex], lineOrdered, lineOrderedStyle));
      maxWidth = Math.max(maxWidth, text.font_size * constants.text.relativeFontWidth * order.toString().length);
    }

    this.paragraphParams = {
      startIndex,
      lineAnchor,
      lineBullet,
      lineOrdered,
      lineBulletStyle,
      lineOrderedStyle,
      lineOrderedNumberingStyle,
      lineOrderedRegex,
      orderedStart,
      order,
      bulletContent,
      bulletFill,
      bulletFontSize,
      bulletFontStyle,
      bulletHighlight,
      bulletFontFamily,
      bulletCustomFontId,
      bulletWidth,
      orderedFill,
      orderedFontSize,
      orderedFontFamily,
      orderedFontWeight,
      orderedFontStyle,
      orderedHighlight,
      orderedCustomFontId,
      orderedWidth,
      lineSpacing,
      isButtonTriggered,
      heading,
      mainTextAnchor,
      factor,
      lineMargin,
      firstLineMargin,
      rightMargin,
      paragraphSpacing,
      maxWidth,
      isEol
    };

    return this.paragraphParams;
  }

  #isLast (index, array) {
    return index >= array.length - 1;
  }

  #sanitizeWord (word, isLastWord) {
    return isLastWord ? word : word + constants.text.noBreakSpace;
  }

  #calculateFirstLinePosition () {
    const text = this.text;
    const params = this.paragraphParams;

    // TODO: Complicated problem with oldY
    this.newX = text.direction === 'rtl' ? this.bbox.x + this.bbox.width : this.bbox.x;
    this.newX += params.factor * params.lineMargin;
    this.newY = this.oldY + text.font_size;
    this.oldY = this.newY;
    this.newY += params.paragraphSpacing;
  }

  #initializeParagraphFirstLine (firstLineParams) {
    const text = this.text;
    const {
      lineAnchor,
      lineMargin,
      firstLineMargin,
      rightMargin,
      lineBullet,
      lineOrdered,
      lineBulletStyle,
      lineOrderedStyle,
      lineOrderedNumberingStyle,
      lineOrderedRegex,
      orderedStart,
      heading,
      isButtonTriggered,
      order,
      bulletContent,
      bulletFill,
      bulletFontSize,
      bulletHighlight,
      bulletFontStyle,
      bulletWidth,
      bulletFontFamily,
      bulletCustomFontId,
      orderedWidth,
      orderedFill,
      orderedFontSize,
      orderedFontFamily,
      orderedFontWeight,
      orderedHighlight,
      orderedFontStyle,
      orderedCustomFontId
    } = firstLineParams;

    const isNewLine = this.lineIndex > 0 ? true : !this.text.isContinuing;
    text.strings[this.lineIndex] = new INKIVE.Text.String({
      x: this.newX,
      y: this.newY,
      isNewLine,
      textAnchor: lineAnchor,
      line_margin: lineMargin,
      first_line_margin: firstLineMargin,
      right_margin: rightMargin,
      bulletLevel: lineBullet,
      bulletStyle: lineBulletStyle,
      orderedLevel: lineOrdered,
      orderedStyle: lineOrderedStyle,
      orderedNumberingStyle: lineOrderedNumberingStyle,
      orderedRegex: lineOrderedRegex,
      orderedStart,
      isButtonTriggered,
      heading,
      order,
      bulletContent,
      orderedWidth,
      orderedFontSize,
      orderedFill,
      orderedFontFamily,
      orderedFontWeight,
      orderedHighlight,
      orderedFontStyle,
      orderedCustomFontId,
      bulletWidth,
      bulletFontSize,
      bulletFontStyle,
      bulletHighlight,
      bulletFill,
      bulletFontFamily,
      bulletCustomFontId,
      startIndex: this.lineIndex - 1 >= 0 ? text.strings[this.lineIndex - 1].endIndex : 0
    });

    this.#shouldAbortRendering(true);
    if (this.shouldAbortRendering) {
      text.strings.splice(this.lineIndex, 1);
    }
  }

  #isBlankLineRendered (paragraph, lineIndex) {
    const text = this.text;
    const canvas = this.canvas;

    if (paragraph === constants.text.noBreakSpace || paragraph === '') {
      text.strings[lineIndex].textContent = constants.text.noBreakSpace;
      text.strings[lineIndex].isBlank = true;
      text.strings[lineIndex].isEol = true;
      text.strings[lineIndex].render(canvas, text);
      return true;
    }

    text.strings[lineIndex].isBlank = false;
    return false;
  }

  #calculateMainLineMargin () {
    const text = this.text;
    const params = this.paragraphParams;

    const mainLineMargin = text.strings[this.lineIndex].isNewLine && params.firstLineMargin && params.factor > -1
      ? params.factor * params.firstLineMargin
      : params.factor * params.lineMargin;

    text.strings[this.lineIndex].x = this.bbox.x + mainLineMargin;
    return mainLineMargin;
  }

  #calculateMainRightMargin () {
    const text = this.text;
    const params = this.paragraphParams;

    const mainRightMargin = text.strings[this.lineIndex].isNewLine && params.firstLineMargin && params.factor < 1
      ? params.factor * params.firstLineMargin
      : params.factor * params.rightMargin;

    return mainRightMargin;
  }

  #getTotalLetterSpacing (startIndex, endIndex, totalWordSpacing, stringContent) {
    const text = this.text;
    if (this.paragraphParams.mainTextAnchor.includes('justify')) return 0;

    const numOfLetters = totalWordSpacing
      ? stringContent.split(constants.text.noBreakSpace).join('').length
      : Math.abs(endIndex - startIndex - 1) || 0;

    return Array.isArray(text.letter_spacing)
      ? this.#getSum(text.letter_spacing.slice(startIndex, endIndex))
      : text.letter_spacing * numOfLetters;
  }

  #getSum (array) {
    let sum = 0;
    for (let i = 0; i < array.length; i++) {
      sum += array[i];
    }

    return sum;
  }

  #getTotalWordSpacing (content) {
    const text = this.text;

    const numOfWords = Math.abs(content.trimEnd().split(constants.text.noBreakSpace).length - 1);
    return Array.isArray(text.word_spacing)
      ? 0
      : text.word_spacing * numOfWords || 0;
  }

  #calculateLineMaxWidth (mainLineMargin, mainRightMargin) {
    const text = this.text;
    const params = this.paragraphParams;

    const stringContent = text.strings[this.lineIndex].textContent;
    // const fakeStartIndex = this.#realCharIndexToFake(params.startIndex);
    // const fakeEndIndex = this.#realCharIndexToFake(params.startIndex + text.strings[this.lineIndex].textContent.length);
    const fakeStartIndex = text.strings[this.lineIndex].startIndex;
    const fakeEndIndex = text.strings[this.lineIndex].startIndex + stringContent.length;
    const totalWordSpacing = this.#getTotalWordSpacing(stringContent);
    const totalLetterSpacing = this.#getTotalLetterSpacing(fakeStartIndex, fakeEndIndex, totalWordSpacing, stringContent);

    return params.maxWidth - totalWordSpacing - totalLetterSpacing - Math.abs(mainLineMargin) - Math.abs(mainRightMargin);
  }

  #checkLineIsFull (isLastWord = false) {
    const text = this.text;
    const params = this.paragraphParams;

    const lineBBox = INKIVE.Text.Helpers.getLineBBox(text, params.startIndex, text.strings[this.lineIndex].textContent);
    if (!lineBBox) return null;

    const mainLineMargin = this.#calculateMainLineMargin();
    const mainRightMargin = this.#calculateMainRightMargin();

    const lineMaxWidth = this.#calculateLineMaxWidth(mainLineMargin, mainRightMargin);
    const isLineFull = lineBBox.width >= lineMaxWidth;

    if (lineBBox.width < lineMaxWidth) {
      text.strings[this.lineIndex].stringBox = lineBBox;
    }

    return isLastWord ? isLineFull : isLineFull || null;
  }

  #offsetNewLine (startIndex, lineIndex) {
    const text = this.text;
    const lineFontSize = text.strings[lineIndex] && text.getLineFontSize(startIndex, startIndex + text.strings[lineIndex].textContent.length);
    if (!lineFontSize) return;

    const verticalOffset = lineFontSize - text.font_size;
    this.oldY += verticalOffset;
    text.strings[lineIndex].y += verticalOffset;
  }

  #shouldAbortRendering () {
    const text = this.text;
    const parent = this.parent;

    if (!parent.child_text || !text.isTextOverflowRendering) {
      this.shouldAbortRendering = false;
      return;
    }

    this.shouldAbortRendering = text.strings[this.lineIndex].y > text.rectBBox.y + text.rectBBox.height;
  }

  #renderCurrentLine () {
    const text = this.text;
    const canvas = this.canvas;
    const startIndex = INKIVE.Text.Helpers.getTextLengthBeforeLineIndex(text, this.lineIndex);

    this.#offsetNewLine(startIndex, this.lineIndex);
    this.#shouldAbortRendering();

    if (this.shouldAbortRendering) {
      return;
    }

    text.strings[this.lineIndex].render(canvas, text);
  }

  #initializeNewLine () {
    const params = this.paragraphParams;
    const {
      lineMargin,
      rightMargin,
      lineSpacing,
      lineAnchor,
      paragraphSpacing,
      isButtonTriggered,
      heading,
      lineBullet,
      bulletFill,
      bulletFontSize,
      bulletHighlight,
      bulletFontStyle,
      bulletWidth,
      bulletFontFamily,
      bulletCustomFontId,
      lineOrdered,
      lineBulletStyle,
      lineOrderedStyle,
      lineOrderedNumberingStyle,
      lineOrderedRegex,
      orderedStart,
      order,
      orderedWidth,
      orderedFontSize,
      orderedFill,
      orderedFontFamily,
      orderedFontWeight,
      orderedFontStyle,
      orderedHighlight,
      orderedCustomFontId,
      bulletContent,
      factor
    } = params;

    this.lineIndex += 1;
    this.oldY += this.text.font_size + lineSpacing;

    this.text.strings[this.lineIndex] = new INKIVE.Text.String({
      x: this.text.direction === 'rtl' ? this.bbox.x + this.bbox.width + factor * lineMargin : this.bbox.x + factor * lineMargin,
      y: this.oldY + paragraphSpacing,
      bulletLevel: lineBullet,
      bulletStyle: lineBulletStyle,
      textAnchor: lineAnchor,
      isButtonTriggered,
      heading,
      orderedLevel: lineOrdered,
      orderedStyle: lineOrderedStyle,
      orderedNumberingStyle: lineOrderedNumberingStyle,
      orderedRegex: lineOrderedRegex,
      orderedStart,
      order,
      bulletContent,
      orderedWidth,
      orderedFontSize,
      orderedFill,
      orderedFontFamily,
      orderedFontWeight,
      orderedHighlight,
      orderedFontStyle,
      orderedCustomFontId,
      bulletWidth,
      bulletFontSize,
      bulletFontStyle,
      bulletHighlight,
      bulletFill,
      bulletFontFamily,
      bulletCustomFontId,
      startIndex: this.lineIndex - 1 >= 0 ? this.text.strings[this.lineIndex - 1].endIndex : 0,
      line_margin: lineMargin,
      right_margin: rightMargin,
      isEol: false
    });
  }

  #renderNewLine (wordIndex, words, charIndex) {
    const text = this.text;
    const params = this.paragraphParams;

    const isLongText = typeof charIndex === 'undefined';
    const slicedWord = isLongText ? words[wordIndex] : words[wordIndex][charIndex];
    const sliceIndex = isLongText ? -(slicedWord.length) : -1;

    // Slice the redundant part out of the line - aka last added word or character
    text.strings[this.lineIndex].textContent = text.strings[this.lineIndex].textContent.slice(0, sliceIndex);
    text.strings[this.lineIndex].textAnchor = params.lineAnchor;
    text.strings[this.lineIndex].bulletLevel = params.lineBullet;
    text.strings[this.lineIndex].orderedLevel = params.lineOrdered;
    text.strings[this.lineIndex].isButtonTriggered = params.isButtonTriggered;
    text.strings[this.lineIndex].heading = params.heading;

    const isCurrentLineEmpty = text.strings[this.lineIndex].textContent === '';
    if (!isCurrentLineEmpty || !isLongText) {
      this.#renderCurrentLine(isLongText);
      this.#initializeNewLine();
    }

    // The sliced word will be added to new initialized line
    // or be added back if current line is empty
    text.strings[this.lineIndex].textContent += slicedWord;
  }

  #handleShortTextRendering (wordIndex, words) {
    const text = this.text;
    const params = this.paragraphParams;
    const word = words[wordIndex];

    text.strings[this.lineIndex].textContent = text.strings[this.lineIndex].textContent.slice(0, -(word.length));

    for (let charIndex = 0; charIndex < word.length; charIndex++) {
      // Add character by character to render
      text.strings[this.lineIndex].textContent += word[charIndex];
      params.startIndex = INKIVE.Text.Helpers.getTextLengthBeforeLineIndex(text, this.lineIndex);
      const isLineFull = this.#checkLineIsFull();
      const hasContent = text.strings[this.lineIndex].textContent.length > 1;

      if (hasContent && isLineFull) {
        this.#renderNewLine(wordIndex, words, charIndex);
      }
    }
  }

  #handleAutoAdjustableRendering (isLineFull) {
    // TODO: Refactor this function
    // TODO: Recalculate y position for text banner
    const text = this.text;
    const parent = this.parent;
    const canvas = this.canvas;
    const autoAdjustableFlag = this.autoAdjustableFlag;

    if (!autoAdjustableFlag || isLineFull === null) return;

    text.strings[this.lineIndex].render(canvas, text);
    text.rectWidth = parent.getWidth();
    if (isLineFull) {
      const delta = text.rectWidth / text.getCRect().width;
      let newFontSize = text.font_size * delta;

      if (text.changing_font_size) {
        newFontSize = text.font_size;
      }

      if (text.auto_adjustable && newFontSize > text.original_font_size) {
        newFontSize = text.original_font_size;
      }

      newFontSize = newFontSize < parent.original_font_size
        ? newFontSize
        : parent.original_font_size;

      text.adjustFontSize(canvas, text, parent, newFontSize);

      if (parent.isSubelement()) {
        while (text.strings[this.lineIndex].getCRect().width > text.rectWidth && newFontSize > 1) {
          newFontSize = newFontSize - newFontSize * 0.1;
          text.adjustFontSize(canvas, text, parent, newFontSize);
        }
      }
      text.strings[this.lineIndex].render(canvas, text);
    } else {
      const delta2 = text.rectWidth / text.strings[this.lineIndex].getCRect().width;
      let newFontSize2 = Number(text.font_size * delta2);

      if (text.auto_adjustable) {
        if (newFontSize2 > text.original_font_size) {
          newFontSize2 = text.original_font_size;
        }

        if (text.newLetters > 0) {
          newFontSize2 = newFontSize2 < text.original_font_size
            ? newFontSize2
            : text.original_font_size;
        }
      } else {
        newFontSize2 = newFontSize2 < parent.original_font_size
          ? newFontSize2
          : parent.original_font_size;
      }

      if (text.font_size && newFontSize2) {
        text.adjustFontSize(canvas, text, parent, newFontSize2);
        text.strings[this.lineIndex].render(canvas, text);
      }
    }
  }

  #renderWords (words) {
    const text = this.text;
    const params = this.paragraphParams;

    for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
      if (this.shouldAbortRendering) break;
      const isLastWord = this.#isLast(wordIndex, words);
      words[wordIndex] = this.#sanitizeWord(words[wordIndex], isLastWord);

      // Add word by word to render
      text.strings[this.lineIndex].textContent += words[wordIndex];

      params.startIndex = INKIVE.Text.Helpers.getTextLengthBeforeLineIndex(text, this.lineIndex);
      const isLineFull = this.#checkLineIsFull(isLastWord);

      // Auto Adjustable is only implemented for Text Banners
      this.#handleAutoAdjustableRendering(isLineFull);

      if (!isLineFull) continue;

      // Here, the text line will be full, last added word would exceed the line
      // we need to move last added word/characters to next line before rendering current line
      this.#renderNewLine(wordIndex, words);
      this.#handleShortTextRendering(wordIndex, words);
    }

    // The last line is only initialized, we need to render it
    this.#renderLastLine(this.lineIndex);
  }

  #renderParagraphs (paragraphs) {
    for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex++) {
      if (this.shouldAbortRendering) break;
      const paragraph = paragraphs[paragraphIndex];
      const isLastParagraph = paragraphIndex === paragraphs.length - 1;
      this.lineIndex += 1;

      const params = this.#calculateParagraphParams(paragraphIndex, isLastParagraph);

      this.#calculateFirstLinePosition();
      this.#initializeParagraphFirstLine(params);
      if (this.shouldAbortRendering) break;

      if (this.#isBlankLineRendered(paragraph, this.lineIndex)) continue;

      const isJapanese = !!paragraph.match(/[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f]/);
      const words = isJapanese ? [paragraph] : paragraph.split(constants.text.noBreakSpace);
      this.#renderWords(words);
    }
  }

  #renderLastLine (lineIndex) {
    const text = this.text;
    const canvas = this.canvas;
    const params = this.paragraphParams;

    this.#offsetNewLine(params.startIndex, lineIndex);
    this.#shouldAbortRendering();

    if (this.shouldAbortRendering) return;

    text.strings[lineIndex].isEol = params.isEol;
    text.strings[lineIndex].render(canvas, text);
  }

  #splitContentIntoParagraphs (content) {
    return content.split(/\r\n|\r|\n/g);
  }

  #setTextCursor () {
    const text = this.text;

    if (text.cursorPosition) {
      text.selectionStart = text.selectionEnd = text.cursorPosition;
    }
  }

  #recalculateTextPosition () {
    const text = this.text;
    const firstLineFontSize = text.getLineFontSize(0, text.strings[0]?.textContent?.length) || text.font_size;

    if (!text.dom) return;

    const textAreaElement = text.dom.parentElement;
    const textElements = textAreaElement.querySelectorAll('text');
    const textHeight = text.strings[text.strings.length - 1].y - text.strings[0].y + firstLineFontSize;
    this.parent.text_box_height = textHeight;

    const delta = INKIVE.Table.getTextVerticalOffset(
      textAreaElement,
      textHeight
    );

    if (delta <= 0) return;

    textElements.forEach(text => {
      const oldPosition = Number.parseFloat(text.getAttribute('y'));
      text.setAttribute('y', oldPosition + delta);
    });
  }

  #addHighlightRects () {
    const text = this.text;
    const canvas = this.canvas;

    text.addHighlightRects(canvas, text.dom);
  }

  #recalculateTextContent () {
    const lastIndex = this.text.dom.childNodes.length - 1;

    this.text.textContent = [...this.text.dom.childNodes].map((t, index) => {
      const ending = (index < lastIndex && t.classList.contains('eol')) ? '\n' : '';
      let lineContent = '';

      if (!t.classList.contains('blank')) {
        for (const node of this.#getContentNodes(t)) {
          lineContent += node.textContent.replace(/ /g, String.fromCharCode(160));
        }
      }

      return lineContent + ending;
    }).join('');
  }

  #recalculateCommonTextSpacing () {
    const text = this.text;
    const attributeMap = constants.text.paragraphAttributesMap;

    for (const attribute in attributeMap) {
      if (!Array.isArray(text[attribute])) continue;

      const numOfParagraph = text.textContent.split('\n').length;
      text[attribute] = TextStylingService.mapCommonStylingToArray(text[attribute], text.textContent, numOfParagraph);
    }
  }

  #getContentNodes (textNode) {
    const tspans = [...textNode.querySelectorAll('tspan')];
    const result = [];

    if (tspans.length === 0) {
      result.push(textNode);
    } else {
      const firstTspanClassList = tspans[0].classList;
      if (firstTspanClassList.contains('ordered') || firstTspanClassList.contains('bullets')) {
        tspans.shift();
      }

      result.push(...tspans.filter(tspan => tspan.children.length === 0));
    }

    return result;
  }

  #calculateHyperLinkBBox () {
    const tspans = [...this.text.dom.querySelectorAll('tspan')];
    const result = [];

    for (const tspan of tspans) {
      const a = tspan.querySelector('a');
      if (!a) continue;

      const bbox = a.getBBox();
      tspan.dataset.boxCoordinates = `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`;
    }

    return result;
  }

  #calculateListArray () {
    if (this.text.isContinuing && this.text.orderedStartArray?.length) this.text.orderedStartArray[0] = 0;

    this.orderArray = TextStylingService.calculateAdvanceOrderContent(
      this.text.orderedArray,
      this.text.orderedStartArray,
      this.text.orderedStyleArray,
      this.orderTracking,
      !!this.text.isContinuing,
      this.text.orderedNumberingStyleArray,
      this.text.orderedRegexArray
    );

    this.bulletContentArray = TextStylingService.calculateBulletContent(
      this.text.bulletArray,
      this.text.bulletStyleArray,
      this.text.customBulletSymbolArray
    );
  }

  renderText () {
    const text = this.text;

    this.#checkTextIsContinuing();
    this.#calculateAdvancedOrderedListTracking();
    this.#calculateTextAttributeArray();
    this.#emptyStringDOM();
    this.#handleTransform();
    this.#setBBox();
    this.#sanitizeSpace();

    const paragraphs = this.#splitContentIntoParagraphs(text.textContent);
    this.#handleShiftingAttributes(paragraphs);
    this.#calculateListArray(paragraphs);
    this.#renderParagraphs(paragraphs);

    // TODO: enable for all. Currently applying only for Text Overflow as this is quite Alpha
    this.parent.child_text && this.#recalculateTextContent();
    this.parent.child_text && this.#recalculateCommonTextSpacing();
    this.#setDataAttributeToDOM();

    this.#setTextCursor();
    this.#recalculateTextPosition();
    this.#addHighlightRects();
    this.#calculateHyperLinkBBox();
  }
}
